import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import classnames from 'classnames'
import {
  Chart,
  ChartLegend,
  ChartTooltip,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartSeries,
  ChartSeriesItem,
  ChartAxisDefaults,
  ChartSeriesItemTooltip,
  ChartArea,
  ChartValueAxis,
  ChartValueAxisItem,
} from '@progress/kendo-react-charts'

import Box from '@mui/material/Box'
import ThreeDotsLoadingSpinner from 'components/Common/LoadingSpinner/threeDots'

import 'hammerjs'
import FinancialOverviewPlaceholder from '../FinancialOverviewPlaceholder'

import ConnectButton from 'components/Common/Button/ConnectButton'

import { formatCurrency } from 'utils/user'
import { feedDataPerDate } from 'utils/object'

function PayableChart() {
  const graphImage = process.env['REACT_APP_BRC_IMAGE_URL'] + '/svg/cashflow_popup_cashflow.svg'
  const tableImage = process.env['REACT_APP_BRC_IMAGE_URL'] + '/svg/cashlow_popup_cashflow_invoices_table.svg'
  const codatConnected = useSelector(state => state.appReducer.accountCodatConnected)
  const daySpanSelected = useSelector(state => state.appReducer.financialOverviewDaySpan)

  const payablesChartData = useSelector(state => state.apiResponseReducers.payablesChartData)
  const loadingPayablesGraphData = useSelector(state => state.apiResponseReducers.loadingPayblesChartData)

  const payablesSummaryData = useSelector(state => state.apiResponseReducers.payablesSummary)

  const seriesStyle = `smooth`

  const [chartType, setChartType] = useState('line')
  const [formattedChartData, setFormattedChartData] = useState([])

  useEffect(() => {
    if (payablesChartData) {
      const originalData = payablesChartData.map(data => ({
        ...data,
        timestamp: new Date(data.date),
        open_line_chart_data: Math.abs(data?.partial_paid_bill),
        paid_line_chart_data: Math.abs(data?.paid_bill),
      }))

      // setFormattedChartData(originalData)
      setFormattedChartData(
        feedDataPerDate(originalData, daySpanSelected, 'date', ['open_line_chart_data', 'paid_line_chart_data']),
      )
    }
  }, [payablesChartData, daySpanSelected])

  return (
    <div className="d-flex flex-column k-relative">
      <div
        style={{
          display: 'inline-flex',
          position: 'absolute',
          zIndex: 2,
          bottom: 0,
          alignSelf: 'flex-end',
          padding: '1rem',
        }}
      >
        <span
          className={classnames('button k-icon k-i-chart-column-clustered k-cursor-pointer text-bold text-xxxl', {
            'color-blue': chartType === 'column',
          })}
          onClick={() => setChartType('column')}
        ></span>
        <span
          className={classnames('button k-icon k-i-chart-line k-cursor-pointer text-bold text-xxxl', {
            'color-blue': chartType === 'line',
          })}
          onClick={() => setChartType('line')}
        ></span>
      </div>
      {!loadingPayablesGraphData && (
        <Fragment>
          {!codatConnected && (
            <Fragment>
              <FinancialOverviewPlaceholder
                isReceivable
                isWithDropPlaceholder
                graphImage={graphImage}
                tableImage={tableImage}
              />
              <div className=" bg-transparent border-light align-text-bottom text-center mt-3">
                <ConnectButton label="Connect your accounting application" isConnected={codatConnected} />
                <p className="p-3 disclaimer-text">
                  When you connect your accounting application you will see your past transactions.
                </p>
              </div>
            </Fragment>
          )}

          {codatConnected && (
            <div>
              <Chart className="flex-grow-1">
                <ChartLegend position="bottom" spacing={40} />
                <ChartTooltip />
                <ChartArea background="rgb(240,244,246)" />

                <ChartAxisDefaults
                  line={{
                    color: 'grey',
                  }}
                  minorGridLines={{
                    visible: false,
                  }}
                  majorGridLines={{
                    visible: false,
                  }}
                  minorTicks={{ visible: false }}
                  majorTicks={{ visible: false }}
                  visible={true}
                />

                <ChartValueAxis>
                  <ChartValueAxisItem
                    labels={{
                      format: 'c0',
                    }}
                  />
                </ChartValueAxis>
                <ChartCategoryAxis>
                  <ChartCategoryAxisItem
                    baseUnits={'days'}
                    maxDivisions={15}
                    majorGridLines={{
                      visible: true,
                    }}
                  />
                </ChartCategoryAxis>
                <ChartLegend position="bottom" labels={{ color: '#25245F' }} />
                <ChartSeries>
                  <ChartSeriesItem
                    style={seriesStyle}
                    type={chartType}
                    data={formattedChartData}
                    field="open_line_chart_data"
                    categoryField="timestamp"
                    color="#0099FF"
                    line={{ width: 4 }}
                    name={`Open: ${
                      payablesSummaryData?.total_open_amount
                        ? formatCurrency(parseFloat(payablesSummaryData.total_open_amount))
                        : ''
                    }`}
                    markers={{ visible: false }}
                    // name={`Open: ${formatCurrency(calSum(openLineChartData, 'open_line_chart_data'))}`}
                  >
                    <ChartSeriesItemTooltip background="#0099FF" format="c2" />
                  </ChartSeriesItem>
                  <ChartSeriesItem
                    style={seriesStyle}
                    type={chartType}
                    data={formattedChartData}
                    field="paid_line_chart_data"
                    categoryField="timestamp"
                    color="#00CBA9"
                    line={{ width: 4 }}
                    name={`Paid: ${
                      payablesSummaryData?.total_paid_amount
                        ? formatCurrency(parseFloat(payablesSummaryData.total_paid_amount))
                        : ''
                    }`}
                    // name={`Paid: ${formatCurrency(calSum(paidLineChartData, 'paid_line_chart_data'))}`}
                    markers={{ visible: false }}
                    highlight={{
                      visible: true,
                    }}
                  >
                    <ChartSeriesItemTooltip background="#00CBA9" format="c2" />
                  </ChartSeriesItem>
                </ChartSeries>
              </Chart>
            </div>
          )}
        </Fragment>
      )}
      {loadingPayablesGraphData && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="400px"
          sx={{
            background: 'rgb(240, 244, 246)',
          }}
        >
          <ThreeDotsLoadingSpinner width="60px" />
        </Box>
      )}
    </div>
  )
}

export default PayableChart
